import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { AuthConfig, useAuthConfig } from "@theprelude/core";
import { ODataRequestParams, PaginatedResponse } from "@theprelude/sdk";

interface PaginationOptions {
  pageParam?: number;
}

type ODataRequestFn<T, R extends PaginatedResponse<T>> = (
  authConfig: AuthConfig,
  params: ODataRequestParams<T>,
) => Promise<R>;

export const useInfiniteODataQuery = <
  T,
  R extends PaginatedResponse<T> = PaginatedResponse<T>,
>({
  key,
  params,
  fetch,
}: {
  key: string;
  params: ODataRequestParams<T>;
  fetch: ODataRequestFn<T, R>;
}) => {
  const authConfig = useAuthConfig();

  return useInfiniteQuery({
    queryKey: [authConfig, "scm", "paginated", key, params],
    queryFn: async ({ pageParam = 0 }: PaginationOptions = {}): Promise<R> => {
      const data = await fetch(authConfig, {
        ...params,
        query: { ...params.query, skip: pageParam },
      });
      return data;
    },
    initialPageParam: 0,
    getNextPageParam,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    select: (data) => flattenPages<T, R>(data),
  });
};

const getNextPageParam = <T, R extends PaginatedResponse<T>>(
  lastPage: R,
  allPages: R[],
) => {
  const fetchedCount = allPages
    .map((r) => r.data.length)
    .reduce((sum, cur) => sum + cur, 0);
  return fetchedCount < lastPage.total_count ? fetchedCount : undefined;
};

const flattenPages = <T, R extends PaginatedResponse<T>>(
  allPages: InfiniteData<R, number>,
) => {
  const data = allPages.pages.flatMap((page) => page.data);
  const totalCount = allPages.pages[0]?.total_count ?? 0;

  return { data, totalCount };
};
